/**
 * The shot log (SEC-001).
 *
 * Every shot the player takes, in order, as `{ dx, dy, power, spinX, spinY }` — which
 * is EXACTLY the payload the Phase 4 Worker will replay (ARCHITECTURE.md §6.3). There
 * is no translation step later, because there is nothing here that is not already the
 * submission format.
 *
 * ⚠️ What is deliberately absent is the point of the file. There is **no score field,
 * no par, no "cleared" flag, no ball-count, no timing** — nothing the player achieved.
 * A run submission carries what the player DID, never what they got, so there is
 * nothing in it to lie in (PRD N3). The Worker replays the shots and computes the
 * score itself, or the run does not count. If a future task ever wants to add a field
 * here, the question to ask first is "could a cheating client benefit from setting
 * it?" — and if so, the answer is that the server derives it instead.
 *
 * Shots are validated on the way IN, with the same `validateShot` the Worker runs, so
 * the client cannot record a log the server would refuse. A player whose log is
 * rejected on a technicality has lost a run they legitimately played.
 */
import {
  DEFAULT_PHYSICS,
  GENERATOR_VERSION,
  PHYSICS_VERSION,
  validateShot,
  type PhysicsConfig,
  type ShotInput,
  type TableSizeFt,
} from '@potted/sim';

/**
 * A run, as it would be submitted.
 *
 * The identifying fields say WHICH table was played — in Phase 1 that is the lab's
 * seed; from Phase 3 it becomes a level id the server already holds. Either way they
 * identify, they never assert an outcome.
 */
export interface RecordedRun {
  readonly physicsVersion: number;
  readonly generatorVersion: number;
  readonly seed: string;
  readonly sizeFt: TableSizeFt;
  readonly ballCount: number;
  readonly shots: readonly ShotInput[];
}

export interface RunIdentity {
  readonly seed: string;
  readonly sizeFt: TableSizeFt;
  readonly ballCount: number;
}

export interface ShotLog {
  /** Append a shot. Throws if the sim would reject it, rather than logging it anyway. */
  record(shot: ShotInput): void;
  shots(): readonly ShotInput[];
  /** The whole run, ready to submit. A copy: the caller cannot reach back in here. */
  toRecordedRun(): RecordedRun;
  reset(): void;
}

export function createShotLog(
  identity: RunIdentity,
  config: PhysicsConfig = DEFAULT_PHYSICS,
): ShotLog {
  const shots: ShotInput[] = [];

  return {
    record(shot) {
      validateShot(shot, config);
      // Copied field by field, so nothing extra a caller happened to be carrying on
      // its object — a score, a timestamp, an id — can ride along into the payload.
      shots.push({
        dx: shot.dx,
        dy: shot.dy,
        power: shot.power,
        spinX: shot.spinX,
        spinY: shot.spinY,
      });
    },

    shots: () => shots.slice(),

    toRecordedRun: () => ({
      physicsVersion: PHYSICS_VERSION,
      generatorVersion: GENERATOR_VERSION,
      seed: identity.seed,
      sizeFt: identity.sizeFt,
      ballCount: identity.ballCount,
      shots: shots.slice(),
    }),

    reset() {
      shots.length = 0;
    },
  };
}
